// Tron on Bitquery GraphQL v2. Shared by the backend tracer and the frontend
// "Get API" button, so keep it free of node-only imports.

export const TRON_NATIVE_SYMBOL = "TRX"
// base58 of 0x41 + 20 zero bytes: sender of mints, receiver of burns
export const TRON_ZERO_ADDRESS = "T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb"
// Wrapped TRX: native TRX is priced by its DEX trades
export const WTRX_CONTRACT = "TNUC9Qb1rRpS5CbWLmNMxXBjyFoydXjWFR"
// coinpath.state keeps native TRX under this currency address (as v1 did)
export const TRON_NATIVE_STATE_ADDRESS = "0"

const BASE58_ADDRESS = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
// TRC-10 token ids start at 1000001
const TRC10_ASSET_ID = /^[1-9]\d{6,}$/

// Every way a flow or a caller names native TRX: its symbol, "-" (address
// stats), "_" (Tronscan) and the "0" coinpath.state stores it under.
const NATIVE_ALIASES = new Set(["trx", "-", "_", "0", "0x"])

/**
 * Tron currencies come in three kinds that v2 tells apart differently:
 * native TRX (`Currency.Native`), TRC-20 contracts (`Currency.SmartContract`,
 * base58) and TRC-10 tokens (`Currency.AssetId`, a number; their SmartContract
 * is empty, like TRX's). Flows store TRX as its symbol, a TRC-20 as its
 * contract and a TRC-10 as its token id. Anything else is dropped — a symbol
 * other than TRX is not an identifier (spam tokens reuse "USDT" etc.).
 */
export interface TronCurrencyFilter {
    native: boolean
    contracts: string[]
    assetIds: string[]
}

export function toTronCurrencyFilter(currency: string[] | undefined): TronCurrencyFilter {
    const filter: TronCurrencyFilter = { native: false, contracts: [], assetIds: [] }
    for (const raw of currency ?? []) {
        const value = raw.trim()
        if (NATIVE_ALIASES.has(value.toLowerCase())) {
            filter.native = true
        } else if (BASE58_ADDRESS.test(value)) {
            if (!filter.contracts.includes(value)) filter.contracts.push(value)
        } else if (TRC10_ASSET_ID.test(value)) {
            if (!filter.assetIds.includes(value)) filter.assetIds.push(value)
        }
    }
    return filter
}

export const isEmptyTronCurrencyFilter = (filter: TronCurrencyFilter) =>
    !filter.native && filter.contracts.length === 0 && filter.assetIds.length === 0

// `where` clause matching any currency of the filter, with `$contracts` /
// `$assetIds` variables for the kinds present (an empty list is never sent).
export function tronCurrencyCondition(filter: TronCurrencyFilter): string {
    const branches = [
        filter.native ? "{ Transfer: { Currency: { Native: true } } }" : "",
        filter.contracts.length ? "{ Transfer: { Currency: { SmartContract: { in: $contracts } } } }" : "",
        filter.assetIds.length ? "{ Transfer: { Currency: { AssetId: { in: $assetIds } } } }" : "",
    ].filter(Boolean)
    return branches.length ? `any: [${branches.join(", ")}]` : ""
}

export const tronCurrencyVariableDefinitions = (filter: TronCurrencyFilter) => [
    filter.contracts.length ? "$contracts: [String!]" : "",
    filter.assetIds.length ? "$assetIds: [String!]" : "",
].filter(Boolean).join("\n        ")

export const tronCurrencyVariables = (filter: TronCurrencyFilter) => ({
    ...(filter.contracts.length ? { contracts: filter.contracts } : {}),
    ...(filter.assetIds.length ? { assetIds: filter.assetIds } : {}),
})

// The first hop is fetched in full up to this cap (see coinpath/tron/tronV2.ts)
export const TRON_MAX_TRANSFERS_FIRST_HOP = 10000

// Depth-1 query for the "Get API" button: what the first hop of a flow asks
// v2 for, runnable as-is in the IDE with `tronApiVariables`.
const apiDirection = (alias: "outbound" | "inbound", currencyCondition: string) => `
        ${alias}: Transfers(
            where: {
                Block: { Time: { since: $from, till: $till } }
                Transfer: { ${alias === "outbound" ? "Sender" : "Receiver"}: { in: $addresses }, Success: true }
                ${currencyCondition}
            }
            orderBy: { descending: Transfer_AmountInUSD }
            limit: { count: $limit }
        ) {
            Block { Time }
            Transaction { Hash }
            Transfer {
                Sender
                Receiver
                Amount
                AmountInUSD
                Currency { Symbol Name SmartContract AssetId Native Decimals }
            }
        }`

export function tronV2ApiQuery(filter: TronCurrencyFilter): string {
    const currencyCondition = tronCurrencyCondition(filter)
    const currencyVariables = tronCurrencyVariableDefinitions(filter)
    return `
    query MoneyFlow(
        $addresses: [String!]
        $from: DateTime
        $till: DateTime
        ${currencyVariables}
        $limit: Int = ${TRON_MAX_TRANSFERS_FIRST_HOP}
    ) {
        Tron(dataset: combined) {${
    apiDirection("outbound", currencyCondition)}${apiDirection("inbound", currencyCondition)}
        }
    }
`
}
